import { useEffect, useRef, useState } from 'react'
import { burstConfetti, celebrationPalette, originOf } from '@/lib/celebrate'
import { FOCUS_RING } from '@/lib/focus-ring'
import { playUnlockSound } from '@/lib/sounds'
import { cn } from '@/lib/utils'
import { SparkleIcon, XIcon } from '@phosphor-icons/react'
import { m as motion, useReducedMotion } from 'framer-motion'
import { createPortal } from 'react-dom'

import type { UnlockPresentation } from './unlock-copy'
import { BadgeGlyph } from './BadgeGlyph'

import './engagement.css'

/** How long an unlock stays up on its own (hover or focus pauses it). */
export const UNLOCK_AUTO_DISMISS_MS = 7000
/** The badge's victory spin plays this long after the button before closing. */
const CELEBRATE_THEN_CLOSE_MS = 650

const EASE_OUT = [0.32, 0.72, 0, 1] as const

/** Twinkles placed around the badge (percent of the stage, delay seconds). */
const SPARKLES = [
  { left: '12%', top: '14%', size: 14, delay: 0 },
  { left: '4%', top: '56%', size: 10, delay: 0.5 },
  { left: '30%', top: '0%', size: 9, delay: 0.9 },
  { left: '80%', top: '10%', size: 13, delay: 0.25 },
  { left: '90%', top: '52%', size: 10, delay: 0.7 },
  { left: '72%', top: '84%', size: 11, delay: 1.2 },
  { left: '20%', top: '84%', size: 9, delay: 1.5 },
] as const

export interface UnlockMomentProps {
  unlock: UnlockPresentation
  /** Unlocks still queued after this one. */
  remaining?: number
  onDismiss: () => void
  onViewAll: () => void
}

/**
 * The big moment: the badge springs in with its tier ring drawing, sparkles
 * twinkle around it, and a chime plays. The yolk button celebrates on the way
 * out — a confetti fountain from the button while the badge does a victory
 * spin — then the next queued unlock (if any) takes the stage.
 *
 * Reduced motion: no confetti or spin, everything already in place, and the
 * button closes immediately.
 */
export function UnlockMoment({
  unlock,
  remaining = 0,
  onDismiss,
  onViewAll,
}: UnlockMomentProps) {
  const reduceMotion = useReducedMotion()
  const ctaRef = useRef<HTMLButtonElement>(null)
  const [isPaused, setIsPaused] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  // Latest dismiss handler, so parent re-renders never restart the timers.
  const dismissRef = useRef(onDismiss)
  useEffect(() => {
    dismissRef.current = onDismiss
  }, [onDismiss])

  // Arrival: chime + confetti, once per unlock.
  useEffect(() => {
    playUnlockSound()
    if (reduceMotion) return
    const timer = window.setTimeout(() => {
      void burstConfetti({ colors: celebrationPalette(unlock.tier) })
    }, 250)
    return () => window.clearTimeout(timer)
  }, [unlock.id, unlock.tier, reduceMotion])

  // Focus the button; give focus back when the moment closes.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    ctaRef.current?.focus({ preventScroll: true })
    return () => previous?.focus?.({ preventScroll: true })
  }, [unlock.id])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismissRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (isPaused || isClosing) return
    const timer = window.setTimeout(
      () => dismissRef.current(),
      UNLOCK_AUTO_DISMISS_MS,
    )
    return () => window.clearTimeout(timer)
  }, [isPaused, isClosing, unlock.id])

  /** The fun exit: confetti from the button, the badge spins, then close. */
  const celebrateAndClose = () => {
    if (isClosing) return
    if (reduceMotion) {
      dismissRef.current()
      return
    }
    setIsClosing(true)
    void burstConfetti({
      shape: 'fountain',
      particleCount: 45,
      origin: originOf(ctaRef.current),
      colors: celebrationPalette(unlock.tier),
    })
    window.setTimeout(() => dismissRef.current(), CELEBRATE_THEN_CLOSE_MS)
  }

  const titleId = `unlock-title-${unlock.id}`
  const lineId = `unlock-line-${unlock.id}`

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/60 px-4 backdrop-blur-[3px]"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.2 } }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
      onClick={() => dismissRef.current()}
      data-testid="unlock-moment"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={lineId}
        onClick={event => event.stopPropagation()}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        className="rounded-shell relative flex w-full max-w-[380px] flex-col items-center border border-border/60 bg-background px-6 pb-5 pt-5 text-center"
        initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -12, scale: 0.94 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => dismissRef.current()}
          className={cn(
            'transition-tonal duration-fast absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground',
            FOCUS_RING,
          )}
        >
          <XIcon className="h-4 w-4" weight="bold" />
        </button>
        {remaining > 0 && (
          <span className="absolute left-4 top-4 rounded-full bg-muted/60 px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
            {remaining} more
          </span>
        )}

        {/* Stage: the badge and its sparkles */}
        <div className="relative mt-5 h-[168px] w-[220px]">
          <span
            aria-hidden
            className="unlock-halo absolute left-1/2 top-1/2 -ml-[74px] -mt-[74px] h-[148px] w-[148px] rounded-full"
            style={{ background: `hsl(var(--badge-${unlock.tier}-soft))` }}
          />
          {!reduceMotion &&
            SPARKLES.map(sparkle => (
              <svg
                key={`${sparkle.left}-${sparkle.top}`}
                aria-hidden
                viewBox="0 0 24 24"
                width={sparkle.size}
                height={sparkle.size}
                className="unlock-sparkle absolute"
                style={{
                  left: sparkle.left,
                  top: sparkle.top,
                  animationDelay: `${sparkle.delay}s`,
                  color: `hsl(var(--badge-${unlock.tier}))`,
                }}
              >
                <path
                  d="M12 1.5l2.6 7.9 7.9 2.6-7.9 2.6L12 22.5l-2.6-7.9L1.5 12l7.9-2.6z"
                  fill="currentColor"
                />
              </svg>
            ))}
          <motion.div
            className="absolute left-1/2 top-1/2 -ml-[56px] -mt-[56px]"
            initial={
              reduceMotion ? false : { opacity: 0, scale: 0.5, rotate: -18 }
            }
            animate={
              isClosing
                ? { opacity: 1, scale: [1, 1.18, 1.06], rotate: 360, y: -6 }
                : { opacity: 1, scale: 1, rotate: 0, y: 0 }
            }
            transition={
              isClosing
                ? { duration: 0.6, ease: EASE_OUT }
                : { type: 'spring', stiffness: 300, damping: 16 }
            }
          >
            <BadgeGlyph
              glyph={unlock.glyph}
              tier={unlock.tier}
              size="xl"
              animateRing={!reduceMotion}
            />
          </motion.div>
        </div>

        <motion.div
          className="mt-3 flex flex-col items-center"
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, delay: 0.12, ease: EASE_OUT }}
        >
          <span
            className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
            style={{
              background: `hsl(var(--badge-${unlock.tier}-soft))`,
              color: `hsl(var(--badge-${unlock.tier}-ink))`,
            }}
          >
            {unlock.eyebrow}
          </span>
          <h2
            id={titleId}
            className="mt-2 text-[24px] font-[650] leading-tight tracking-tight text-foreground"
          >
            {unlock.title}
          </h2>
          <p id={lineId} className="mt-1 text-[13px] text-muted-foreground">
            {unlock.line}
          </p>
        </motion.div>

        <motion.button
          ref={ctaRef}
          type="button"
          onClick={celebrateAndClose}
          disabled={isClosing}
          className={cn(
            'mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--sunny-yolk))] px-5 text-[15px] font-semibold text-[hsl(var(--cast-iron-skillet))]',
            FOCUS_RING,
          )}
          whileHover={reduceMotion ? undefined : { scale: 1.03, rotate: -1 }}
          whileTap={reduceMotion ? undefined : { scale: 0.94 }}
          transition={{ type: 'spring', stiffness: 500, damping: 18 }}
        >
          <SparkleIcon className="h-4 w-4" weight="fill" aria-hidden />
          {isClosing ? 'Nice!' : unlock.cta}
          {remaining > 0 && (
            <span className="ml-1 rounded-full bg-[hsl(var(--cast-iron-skillet)/0.12)] px-2 py-0.5 text-xs font-semibold">
              Next →
            </span>
          )}
        </motion.button>
        <button
          type="button"
          onClick={onViewAll}
          className={cn(
            'transition-tonal duration-fast mt-2 rounded-full px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground',
            FOCUS_RING,
          )}
        >
          See all achievements
        </button>
      </motion.div>
    </motion.div>,
    document.body,
  )
}
